import Script from 'next/script';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import env from '@login/tools/environments';

import styles from './turnstile-widget.module.scss';
import type {
	TurnstileSize,
	TurnstileTheme,
	TurnstileWidgetHandle,
} from './turnstile.types';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
	'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileWidgetProps = {
	action: string;
	onSuccess: (token: string) => void;
	onExpire?: () => void;
	onError?: () => void;
	theme?: TurnstileTheme;
	size?: TurnstileSize;
	language?: string;
	ref?: React.Ref<TurnstileWidgetHandle>;
};

export function TurnstileWidget({
	action,
	onSuccess,
	onExpire,
	onError,
	theme = 'light',
	size = 'flexible',
	language = 'en-GB',
	ref,
}: TurnstileWidgetProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<string | null>(null);
	const onSuccessRef = useRef(onSuccess);
	const onExpireRef = useRef(onExpire);
	const onErrorRef = useRef(onError);
	const [isScriptReady, setIsScriptReady] = useState(false);

	onSuccessRef.current = onSuccess;
	onExpireRef.current = onExpire;
	onErrorRef.current = onError;

	useImperativeHandle(ref, () => ({
		reset: () => {
			if (widgetIdRef.current && window.turnstile) {
				window.turnstile.reset(widgetIdRef.current);
			}
		},
	}));

	useEffect(() => {
		if (typeof window !== 'undefined' && window.turnstile) {
			setIsScriptReady(true);
		}
	}, []);

	useEffect(() => {
		if (!isScriptReady || !window.turnstile || !containerRef.current) {
			return;
		}

		const container = containerRef.current;
		const widgetId = window.turnstile.render(container, {
			sitekey: env.PUBLIC_TURNSTILE_SITE_KEY,
			theme,
			size,
			action,
			language,
			callback: (token) => onSuccessRef.current(token),
			'expired-callback': () => onExpireRef.current?.(),
			'error-callback': () => onErrorRef.current?.(),
		});

		widgetIdRef.current = widgetId;

		return () => {
			window.turnstile?.remove(widgetId);
			if (widgetIdRef.current === widgetId) {
				widgetIdRef.current = null;
			}
		};
	}, [isScriptReady, action, theme, size]);

	return (
		<>
			<Script
				id={TURNSTILE_SCRIPT_ID}
				src={TURNSTILE_SCRIPT_SRC}
				strategy="afterInteractive"
				onReady={() => setIsScriptReady(true)}
			/>
			<div ref={containerRef} className={styles.widget} />
		</>
	);
}
